import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useLocation } from 'react-router-dom'

import { customerAccountClosureEndpoint } from 'constants/feature-keys'

import { useFeature } from '@acorns/web-toggles-react'

import {
  CustomerAccountClosureState,
  SubscriptionTransitionAction,
  useCustomerAccountClosureQueryQuery,
  useTransitionActionQueryQuery,
} from 'generated/types'

import { PreflightWrapper } from '../config/preflight'
import { CashOwedProvider } from '../providers/CashOwed'
import { ClosureContextProvider } from '../providers/ClosureContext'
import { SessionProvider } from '../providers/SessionProvider'
import { Provider as OpenAccountsProvider } from '../utils/openAccounts'
import { routes } from './routes'

const PENDING_CLOSED_ACCOUNT_ACCESSIBLE_PATHS = [
  routes.settings.subscriptionCenter,
  routes.documents.index,
  routes.documents.documentUploaderIndex,
  routes.invest.fundIndex,
  routes.portfolio.aboutInvestPortfolio,
]

const PendingClosure = ({ children }: { children: JSX.Element }) => {
  const history = useHistory()
  const location = useLocation()

  const [customerAccountClosureEndpointActive] = useFeature(
    customerAccountClosureEndpoint,
  )

  const { data: transitionActionData, loading: transitionActionDataLoading } =
    useTransitionActionQueryQuery({
      fetchPolicy: 'no-cache',
    })

  const {
    data: customerAccountClosureData,
    loading: customerAccountClosureDataLoading,
  } = useCustomerAccountClosureQueryQuery({
    fetchPolicy: 'no-cache',
    skip: !customerAccountClosureEndpointActive,
  })

  useEffect(() => {
    const inDeactivationTransition =
      transitionActionData?.tierSubscriptionByUserId?.transition
        ?.transitionAction === SubscriptionTransitionAction.Deactivate
    const closureRequested =
      customerAccountClosureData?.customerAccountClosureRequest
        ?.closureState === CustomerAccountClosureState.Initiated
    const invalidRouteForDeactivatedAccount =
      !PENDING_CLOSED_ACCOUNT_ACCESSIBLE_PATHS.some((path) =>
        location.pathname.startsWith(path),
      )

    const shouldRedirectDueToRequestedClosure =
      customerAccountClosureEndpointActive &&
      !customerAccountClosureDataLoading &&
      closureRequested
    if (
      !transitionActionDataLoading &&
      (inDeactivationTransition || shouldRedirectDueToRequestedClosure) &&
      invalidRouteForDeactivatedAccount
    ) {
      history.push(routes.settings.subscriptionCenter)
    }
  }, [
    history,
    transitionActionData,
    customerAccountClosureData,
    transitionActionDataLoading,
    customerAccountClosureDataLoading,
    customerAccountClosureEndpointActive,
    location.pathname,
  ])

  return <>{children}</>
}

export { PendingClosure }

const AuthenticatedProviders = ({ children }: { children: JSX.Element }) => (
  <SessionProvider>
    <PreflightWrapper>
      <OpenAccountsProvider>
        <CashOwedProvider>
          <ClosureContextProvider>
            <PendingClosure>{children}</PendingClosure>
          </ClosureContextProvider>
        </CashOwedProvider>
      </OpenAccountsProvider>
    </PreflightWrapper>
  </SessionProvider>
)

export default AuthenticatedProviders
